"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Plus, Pencil, Trash2, RefreshCcw, Sparkles, Upload, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PublishDialog } from "./SkillsMarketplacePage";
import { Row, Col } from "../Common/Row";
import { PageHeader } from "../Common/PageHeader";
// `Surface` is no longer imported directly; `WorkspaceCard` wraps it.
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { tokens } from "@/theme/tokens";
import { useAuthedApi } from "../../api/useAuthedApi";
import type {
  GroupedSkills,
  Skill,
  SkillTier,
} from "../../api/skillsApi";
import type { Department } from "../../api/departmentsApi";
import { parseDeptObjectId } from "../../api/departmentsApi";
import { RestError } from "../../api/restClient";
import { useCurrentUser } from "@/auth/RequireAuth";
import { hasPermission } from "@/auth/useAuth";

type TierKey = keyof GroupedSkills;
const TIERS: TierKey[] = ["user", "department", "company", "platform"];
const TIER_LABEL: Record<TierKey, string> = {
  user: "Personal",
  department: "Department",
  company: "Company",
  platform: "Platform",
};
const EDITABLE_TIERS: Record<TierKey, boolean> = {
  user: true,
  department: true,
  company: true,
  // Platform skills are creatable only by platform admins — the create button
  // is gated at runtime (see createGate); listed platform skills stay read-only
  // for non-admins via the SkillCard `editable` prop.
  platform: true,
};

interface SkillFormState {
  open: boolean;
  mode: "create" | "edit";
  initial?: Skill | null;
  tier: TierKey;
}

export function SkillsPage() {
  const { skills, departments } = useAuthedApi();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [manageableDepts, setManageableDepts] = useState<Department[]>([]);
  const [isDeptEditor, setIsDeptEditor] = useState(false);
  const [data, setData] = useState<GroupedSkills | null>(null);
  const [tab, setTab] = useState<TierKey>("user");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState<SkillFormState>({
    open: false,
    mode: "create",
    tier: "user",
  });
  const [deleting, setDeleting] = useState<Skill | null>(null);
  const [publishing, setPublishing] = useState<Skill | null>(null);

  const refresh = useCallback(async () => {
    if (!skills) return;
    setRefreshing(true);
    setLoadError(null);
    try {
      const d = await skills.list();
      setData(d);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }, [skills]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Load the departments the user can create Department-tier skills for. Prefer
  // the full company list (admins can pick any); fall back to the "mine" set
  // (raw FGA object ids), from which we can only surface ids, not names.
  useEffect(() => {
    if (!departments) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await departments.list();
        if (!cancelled) setManageableDepts(all);
      } catch {
        try {
          const mine = await departments.listMine();
          if (cancelled) return;
          const parsed = mine.department_ids
            .map((oid) => parseDeptObjectId(oid))
            .filter((id): id is number => id != null)
            .map((id) => ({ id, name: `Department ${id}`, parent_id: null }));
          setManageableDepts(parsed);
        } catch {
          if (!cancelled) setManageableDepts([]);
        }
      }
      // Permission signal, independent of the picker list: a non-empty /mine
      // means the user is a department (skill) editor even when list() also
      // succeeded (list() is readable by any authed user, so its success is
      // not evidence of manage rights).
      try {
        const mine = await departments.listMine();
        if (!cancelled) setIsDeptEditor(mine.department_ids.length > 0);
      } catch {
        if (!cancelled) setIsDeptEditor(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departments]);

  // UX-only gating (backend re-enforces): company skills need company:manage;
  // platform skills need platform-admin; department skills need at least one
  // manageable department.
  const canManageCompany = hasPermission(user, "company:manage");
  const isPlatformAdmin = Boolean(user?.is_platform_admin);
  // Department bucket enable: admin OR department skill-editor (/mine
  // non-empty) OR a non-empty department list to pick from. Deliberately
  // permissive — all-departments editors may not show in /mine yet, and the
  // backend re-enforces on POST — so this never hard-blocks beyond a hint.
  const hasDepartments =
    canManageCompany || isDeptEditor || manageableDepts.length > 0;

  const createGate = useMemo<Record<TierKey, { enabled: boolean; reason?: string }>>(
    () => ({
      user: { enabled: true },
      department: hasDepartments
        ? { enabled: true }
        : { enabled: false, reason: "You don't manage any departments yet" },
      company: canManageCompany
        ? { enabled: true }
        : { enabled: false, reason: "Only a company admin can create company skills" },
      platform: isPlatformAdmin
        ? { enabled: true }
        : { enabled: false, reason: "Only a platform admin can create platform skills" },
    }),
    [hasDepartments, canManageCompany, isPlatformAdmin],
  );

  const canCreate = (t: TierKey): boolean => EDITABLE_TIERS[t] && createGate[t].enabled;

  // Show a single source of truth for the visible list — falls back to
  // empty arrays so the rendered DOM stays stable across loads.
  const visible: Skill[] = useMemo(() => data?.[tab] ?? [], [data, tab]);
  const counts: Record<TierKey, number> = useMemo(() => {
    const out: Record<TierKey, number> = {
      user: 0,
      department: 0,
      company: 0,
      platform: 0,
    };
    if (data) for (const t of TIERS) out[t] = data[t].length;
    return out;
  }, [data]);

  return (
    <Box>
      <PageHeader
        title="Skills"
        subtitle="Reusable instructions the assistant follows. Scoped by tier — personal skills override company defaults."
        actions={
          <>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={refresh} disabled={refreshing} aria-label="refresh">
                  <RefreshCcw size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ShoppingBag size={16} />}
              onClick={() => navigate("/workspace/marketplace")}
            >
              Marketplace
            </Button>
            <Tooltip title={canCreate(tab) ? "" : createGate[tab].reason ?? ""}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() =>
                    setForm({ open: true, mode: "create", tier: tab, initial: null })
                  }
                  disabled={!canCreate(tab)}
                >
                  New skill
                </Button>
              </span>
            </Tooltip>
          </>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TierKey)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {TIERS.map((t) => (
          <Tab
            key={t}
            value={t}
            label={
              <Row gap={1} align="center">
                {TIER_LABEL[t]}
                <Chip
                  size="small"
                  label={counts[t]}
                  sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
                />
              </Row>
            }
          />
        ))}
      </Tabs>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load: {loadError}
        </Alert>
      )}

      {refreshing && data === null ? (
        <CardSkeletonList count={4} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={`No ${TIER_LABEL[tab].toLowerCase()} skills yet`}
          description="Skills are reusable instructions the assistant follows on future turns."
          action={
            canCreate(tab) ? (
              <Button
                size="small"
                startIcon={<Plus size={14} />}
                onClick={() => setForm({ open: true, mode: "create", tier: tab })}
              >
                Create one
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Col gap={1}>
          {visible.map((s) => (
            <SkillCard
              key={s.id}
              skill={s}
              tier={tab}
              editable={canCreate(tab)}
              onEdit={() => setForm({ open: true, mode: "edit", tier: tab, initial: s })}
              onDelete={() => setDeleting(s)}
              onPublish={() => setPublishing(s)}
            />
          ))}
        </Col>
      )}

      {form.open && (
        <SkillFormDialog
          mode={form.mode}
          tier={form.tier}
          departments={manageableDepts}
          initial={form.initial ?? null}
          onClose={() => setForm({ ...form, open: false })}
          onSaved={() => {
            setForm({ ...form, open: false });
            refresh();
          }}
        />
      )}

      <DeleteConfirmDialog
        skill={deleting}
        tier={tab}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          refresh();
        }}
      />

      {publishing && (
        <PublishDialog
          prefill={{
            name: publishing.name,
            description: publishing.description,
            body: publishing.body,
          }}
          onClose={() => setPublishing(null)}
          onPublished={() => setPublishing(null)}
        />
      )}
    </Box>
  );
}

function SkillCard({
  skill,
  tier,
  editable,
  onEdit,
  onDelete,
  onPublish,
}: {
  skill: Skill;
  tier: TierKey;
  editable: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPublish: () => void;
}) {
  return (
    <WorkspaceCard>
      <Row align="start" justify="space-between" gap={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Row gap={1} align="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
              {skill.name}
            </Typography>
            <Chip size="small" label={`v${skill.version}`} />
            {skill.source && skill.source !== "user" && (
              <Chip size="small" label={skill.source} variant="outlined" />
            )}
          </Row>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {skill.description}
          </Typography>
          <Typography
            variant="caption"
            component="pre"
            sx={(t) => ({
              m: 0,
              p: 1,
              // Recessed code surface: a real ring (not just a shadow) so it
              // stays visible even in dark mode, where it would otherwise
              // match the card behind it exactly.
              bgcolor: "background.default",
              border: `1px solid ${t.palette.mode === "light" ? "rgba(16,24,40,0.08)" : "rgba(255,255,255,0.09)"}`,
              borderRadius: `${tokens.radius.sm}px`,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 220,
              overflowY: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            })}
          >
            {skill.body}
          </Typography>
        </Box>
        {editable && (
          <Row gap={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip title="Publish to Marketplace">
              <IconButton size="small" onClick={onPublish} aria-label={`publish ${skill.name}`}>
                <Upload size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} aria-label={`edit ${skill.name}`}>
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete} aria-label={`delete ${skill.name}`}>
                <Trash2 size={14} />
              </IconButton>
            </Tooltip>
          </Row>
        )}
      </Row>
    </WorkspaceCard>
  );
}

function SkillFormDialog({
  mode,
  tier,
  departments,
  initial,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  tier: TierKey;
  departments: Department[];
  initial: Skill | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { skills } = useAuthedApi();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  // Department-tier skills carry a required dept_id sourced from the picker.
  // Default to the sole department when there's exactly one to save a click.
  const [deptId, setDeptId] = useState<number | "">(
    departments.length === 1 ? departments[0].id : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsDept = tier === "department" && mode === "create";
  const deptOk = !needsDept || typeof deptId === "number";

  // Live counters mirror the backend's length constraints so the user
  // sees they're inside the bounds without round-tripping a 422.
  const bodyOk = body.length >= 50 && body.length <= 15000;
  const descOk = description.length >= 10 && description.length <= 300;
  const nameOk =
    mode === "edit"
      ? true
      : /^[a-z][a-z0-9_]{0,63}$/.test(name) && name.length >= 2;

  const onSubmit = async () => {
    if (!skills) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === "create") {
        await skills.create({
          name,
          description,
          body,
          tier: tier as SkillTier,
          ...(needsDept && typeof deptId === "number" ? { dept_id: deptId } : {}),
        });
      } else if (initial) {
        await skills.update(initial.name, {
          description,
          body,
          tier: tier as SkillTier,
        });
      }
      onSaved();
    } catch (e) {
      const msg =
        e instanceof RestError ? e.detail : e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "create" ? "New skill" : `Edit ${initial?.name}`}
      </DialogTitle>
      <DialogContent>
        <Col gap={2} sx={{ pt: 1 }}>
          {mode === "create" && (
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              helperText="Lowercase letters, digits, underscores. 2–64 chars."
              error={!nameOk && name.length > 0}
            />
          )}
          {needsDept && (
            <FormControl fullWidth size="small" error={!deptOk}>
              <InputLabel id="skill-dept-label">Department</InputLabel>
              <Select
                labelId="skill-dept-label"
                label="Department"
                value={deptId === "" ? "" : String(deptId)}
                onChange={(e) =>
                  setDeptId(e.target.value === "" ? "" : Number(e.target.value))
                }
              >
                {departments.map((d) => (
                  <MenuItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            helperText={`${description.length}/300 — short summary the model uses to choose when to apply this skill.`}
            error={!descOk && description.length > 0}
          />
          <TextField
            label="Body (instructions)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            helperText={`${body.length}/15000 — full prompt text the assistant follows.`}
            error={!bodyOk && body.length > 0}
            slotProps={{
              htmlInput: { style: { fontFamily: "ui-monospace, monospace", fontSize: 13 } },
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Col>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={saving || !descOk || !bodyOk || !nameOk || !deptOk}
        >
          {saving ? <CircularProgress size={16} /> : mode === "create" ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteConfirmDialog({
  skill,
  tier,
  onClose,
  onDeleted,
}: {
  skill: Skill | null;
  tier: TierKey;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { skills } = useAuthedApi();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (!skill || !skills) return;
    setDeleting(true);
    setError(null);
    try {
      await skills.remove(skill.name, tier as SkillTier);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!skill} onClose={onClose}>
      <DialogTitle>Delete this skill?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          <strong>{skill?.name}</strong> will be removed. The assistant won't apply it on future turns.
          This can't be undone.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={deleting}>
          {deleting ? <CircularProgress size={16} /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
